import type { AttendanceStatus, StudentAttendanceStatus, StaffAttendanceStatus } from '../domain';

export type { AttendanceStatus, StudentAttendanceStatus, StaffAttendanceStatus };

// Two distinct universes:
//  • Students  — short cycle, no salary deltas, no HD/WFH. Just P/A/L/E.
//  • Staff     — full cycle with hours impact, HD/WFH/OT/H for payroll.
const STUDENT_ORDER: StudentAttendanceStatus[] = ['P', 'A', 'L', 'E'];
const STAFF_ORDER: StaffAttendanceStatus[] = ['P', 'HD', 'WFH', 'OT', 'H', 'A', 'L'];

const STUDENT_HOURS: Record<StudentAttendanceStatus, number> = { P: 1, A: 0, L: 0, E: 1 };
const STAFF_HOURS: Record<StaffAttendanceStatus, number> = { P: 8, HD: 4, WFH: 8, OT: 10, H: 4, A: 0, L: 0 };

const LOG_STUDENT: Record<string, StudentAttendanceStatus> = {};
const LOG_STAFF: Record<string, StaffAttendanceStatus> = {};

function studentKey(uid: string, date: string): string { return 'students:' + uid + ':' + date; }
function staffKey(uid: string, date: string): string { return 'employees:' + uid + ':' + date; }

export function cycleMark(actor: 'students' | 'employees', uid: string, date: string): AttendanceStatus {
  if (actor === 'students') {
    const cur: StudentAttendanceStatus = LOG_STUDENT[studentKey(uid, date)] || 'P';
    const next = STUDENT_ORDER[(STUDENT_ORDER.indexOf(cur) + 1) % STUDENT_ORDER.length];
    LOG_STUDENT[studentKey(uid, date)] = next;
    return next;
  }
  const cur: StaffAttendanceStatus = LOG_STAFF[staffKey(uid, date)] || 'P';
  const next = STAFF_ORDER[(STAFF_ORDER.indexOf(cur) + 1) % STAFF_ORDER.length];
  LOG_STAFF[staffKey(uid, date)] = next;
  return next;
}

export function statusClass(st: string): string { return 'st-' + st; }

export function ratioFor(actor: 'students' | 'employees', uid: string, dates: string[]): number {
  if (!dates.length) return 0;
  const presentChars = actor === 'students' ? ['P', 'E'] : ['P', 'WFH', 'H'];
  const log = actor === 'students' ? LOG_STUDENT : LOG_STAFF;
  const k = actor === 'students' ? studentKey : staffKey;
  const present = dates.filter((d) => presentChars.includes((log[k(uid, d)] || 'P') as string)).length;
  return Math.round((present / dates.length) * 100);
}

export function hoursFor(st: string): number {
  if ((STUDENT_HOURS as Record<string, number>)[st] !== undefined) return STUDENT_HOURS[st as StudentAttendanceStatus];
  return (STAFF_HOURS as Record<string, number>)[st] ?? 0;
}

export function getMark(actor: 'students' | 'employees', uid: string, date: string): AttendanceStatus {
  if (actor === 'students') return LOG_STUDENT[studentKey(uid, date)] || 'P';
  return LOG_STAFF[staffKey(uid, date)] || 'P';
}

export function bulkMark(actor: 'students' | 'employees', uid: string, dates: string[], status: AttendanceStatus): void {
  dates.forEach((d) => {
    if (actor === 'students') LOG_STUDENT[studentKey(uid, d)] = status as StudentAttendanceStatus;
    else LOG_STAFF[staffKey(uid, d)] = status as StaffAttendanceStatus;
  });
}

export function labelFor(st: string): string {
  const M: Record<string, string> = { P: 'Present (P)', A: 'Absent (A)', L: 'Late (L)', E: 'Excused (E)',
    HD: 'Half-day (HD)', WFH: 'Work-from-home (WFH)', OT: 'Overtime (OT)', H: 'Holiday (H)' };
  return M[st] || st;
}